import { page } from '$app/state';
import { m } from '$lib/paraglide/messages';
import { decode } from 'entities';

export const thumbnailUrl = (media_base_url: string, path: string, size = 300) => {
	return `${media_base_url}${size}/${path}`;
};

export const mediaUrl = (media_base_url: string, path: string) => {
	return `${media_base_url}${path}`;
};

export const getFlagEmoji = (countryCode: string) => {
	const codePoints = countryCode
		.toUpperCase()
		.split('')
		.map((char) => 127397 + char.charCodeAt(0));
	return String.fromCodePoint(...codePoints);
};

export const getCountryDisplayName = (countryCode: string, language: string) => {
	const regionNames = new Intl.DisplayNames(language, { type: 'region' });

	return regionNames.of(countryCode);
};

export const getErrorMessage = (prefix: string | null, code: number) => {
	return translateDynKey(`${prefix}.${code}`, m['errors.unknown.message']());
};

export const responseResult = async <Success = unknown, Failure = unknown>(
	response: Response,
): Promise<ResponseResult<Success, Failure>> => {
	if (response.ok)
		return { type: 'success', status: response.status, data: (await response.json()) as Success };
	return {
		type: 'failure',
		status: response.status,
		data: response.status >= 500 ? undefined : ((await response.json()) as Failure),
	};
};

export const truncate = (input: string, limit: number): string => {
	const ELLIPSIS = '...';

	const text = html2text(input);
	if (text.length <= limit) return text;

	const hardLimit = limit - ELLIPSIS.length;
	const truncated = text.slice(0, hardLimit);

	const clean = truncated.replace(/\s+[^\s]*$/, '');

	return clean.trimEnd() + ELLIPSIS;
};

export const html2text = (input: string): string => {
	return decode(
		input
			.replace(/<br\s*\/?>/gi, ' ')
			.replace(/<[^>]*>/g, '') // Delete all HTML tags
			.replace(/\s+/g, ' ') // Normalize spaces
			.trim(),
	);
};

export const removeUrlScheme = (url: string): string => url.replace(/^https?:\/\//, '');

export const getNameWithoutBrand = (name: string, brand: string): string => {
	return name.replace(brand, '').replace(/\s\s+/g, ' ');
};

export const formatBytes = (bytes: number, decimals: number) => {
	if (bytes == 0) return '0 Bytes';
	const k = 1000,
		dm = decimals || 2,
		sizes = ['Bytes', 'KB', 'MB'],
		i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const translateOptionTitle = (
	lang: string,
	option: ProductOption | ProductSubOption | ProductOptionValue,
) => option.titles[lang] || option.titles['fr'];

export const translateDynKey = (key: string, fallback?: string) => {
	return key in m ? (m[key as keyof typeof m] as () => string)() : (fallback ?? key);
};

export const translateFilterName = (name: string) => {
	name = name.replace(/_i18n$/, '');

	switch (name) {
		case 'in_stock':
			return m['attributes.shipped_in_24']();
		case 'main_color_code':
			return m['attributes.main_color']();
		case 'variations':
			return m.variations();
		default: {
			return translateDynKey(`attributes.${name}`, name);
		}
	}
};

export const main_colors: Record<string, string> = {
	BLACK: '#010101',
	BLUE: '#1E90FF',
	WHITE: '#FEFEFE',
	RED: '#FF0000',
	GREEN: '#008000',
	YELLOW: '#FFFF00',
	ORANGE: '#FF8904',
	PURPLE: '#8B008B',
	GOLD: '#DAA520',
	GRAY: '#808080',
	PINK: '#FF1493',
	TURQUOISE: '#40E0D0',
	BEIGE: '#FFE4C4',
	MULTICOLOR: 'linear-gradient(to left, blue, green, yellow, orange, red)',
	BROWN: '#8B4513',
	SILVER: 'linear-gradient(#262626, #a3a3a3)',
	BRONZE: '#CD7F32',
};

export const formatPrice = (
	price: number,
	language: string = page.data.store.language,
	currency: string = page.data.store.currency,
) => {
	return new Intl.NumberFormat(language, { style: 'currency', currency }).format(price);
};

export const currencySymbol = (
	language: string = page.data.store.language,
	currency: string = page.data.store.currency,
): { symbol: string; position: 'left' | 'right' } => {
	const formatter = new Intl.NumberFormat(language, {
		style: 'currency',
		currency,
		currencyDisplay: 'symbol',
	});

	const parts = formatter.formatToParts(1);

	const numberIndex = parts.findIndex((p) => p.type === 'integer');
	const currencyIndex = parts.findIndex((p) => p.type === 'currency');

	return {
		symbol: parts[currencyIndex].value,
		position: currencyIndex < numberIndex ? 'left' : 'right',
	};
};

// TODO: Use translation engine instead - with m['brands.slug']() ?
export const brandsSlugs: Record<string, string> = {
	cs: 'znacky',
	da: 'maerker',
	de: 'marken',
	en: 'brands',
	es: 'marcas',
	fr: 'marques',
	hu: 'markak',
	it: 'brands',
	nl: 'merken',
	pl: 'marki',
	pt: 'marcas',
	sv: 'varumarken',
};

export const brandsPageRegex = Object.fromEntries(
	Object.entries(brandsSlugs).map(([lang, slug]) => [lang, new RegExp(`^${slug}/?$`)]),
);

export const categoryBrandPageRegex = Object.fromEntries(
	Object.entries(brandsSlugs).map(([lang, slug]) => [lang, new RegExp(`^${slug}/.+`)]),
);

// TODO: Use translation engine instead - with m['discount_codes.slug']() ?
export const discountCodesSlugs: Record<string, string> = {
	cs: 'promo-kody',
	da: 'rabatkoder',
	de: 'rabattcodes',
	en: 'discount-codes',
	es: 'codigos-promocionales',
	fr: 'codes-promo',
	hu: 'kedvezmenykodok',
	it: 'codici-promozionali',
	nl: 'promotiecodes',
	pl: 'kody-rabatowe',
	pt: 'codigos-de-cupao',
	sv: 'rabattkoder',
};

const dateFormat: Record<string, object> = {
	short: {
		year: 'numeric',
		month: 'numeric',
		day: 'numeric',
	},
	'm-full': {
		weekday: 'short',
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	},
	full: {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	},
};

export const formatDate = (date: Date, locale: string, format: string): string => {
	return date.toLocaleDateString(locale, dateFormat[format] ?? dateFormat['short']);
};

export const hashEmail = async (email: string) => {
	const encoder = new TextEncoder();
	const data = encoder.encode(email);
	return crypto.subtle.digest('SHA-256', data).then((hash) => {
		return Array.from(new Uint8Array(hash))
			.map((b) => b.toString(16).padStart(2, '0'))
			.join('');
	});
};
